import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Select, MenuItem, FormControl, InputLabel, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    PayInterval,
    TimePeriodEndType,
    DurationUnit
} from '../../../utils/financialCalculations'; // Assuming enums are here
import { dateToString, stringToDate} from '../../../utils/utils';


const createDefaultIncomeSource = () => ({
    trackingKey: `income-${Date.now()}-${Math.random()}`,
    description: `Employer`,
    amount: 50000,
    payInterval: PayInterval.PER_YEAR,
    startDate: '',
    endType: TimePeriodEndType.DURATION,
    endDate: '',
    duration: 1,
    durationUnit: DurationUnit.YEARS,
    lostEarningCapacity: 1.0
});


const EconomicLossesStep = ({ formik }) => {
    const { values, setFieldValue, errors, touched, handleBlur } = formik;


    // State for Income Periods Modal
    const [periodsModalOpen, setPeriodsModalOpen] = useState(false);
    const [currentPeriodSource, setCurrentPeriodSource] = useState(null);
    const [editingPeriodIndex, setEditingPeriodIndex] = useState(null);
    const [periodErrors, setPeriodErrors] = useState({});


    // --- Income Periods Handlers ---
    const handleOpenAddPeriodsModal = () => {
        setCurrentPeriodSource(createDefaultIncomeSource());
        setEditingPeriodIndex(null);
        setPeriodsModalOpen(true);
    };

    const handleOpenEditPeriodsModal = (source, index) => {
        setCurrentPeriodSource({ ...source });
        setEditingPeriodIndex(index);
        setPeriodsModalOpen(true);
    };

    const handleClosePeriodsModal = () => {
        setPeriodsModalOpen(false);
        setCurrentPeriodSource(null);
        setEditingPeriodIndex(null);
    };

    const handlePeriodModalChange = (field, value) => {
        setCurrentPeriodSource(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'endType') {
                if (value === TimePeriodEndType.END_DATE) {
                    updated.duration = 0;
                    updated.durationUnit = DurationUnit.YEARS;
                } else if (value === TimePeriodEndType.DURATION) {
                    if (updated.duration == 0) {
                        updated.duration = 1; // default to one
                    }
                    updated.endDate = '';
                }
            }
            return updated;
        });
    };

    useEffect(() => {
        if (periodsModalOpen) {
            validateCurrentPeriod();
        }
    }, [currentPeriodSource, periodsModalOpen]);

    const validateCurrentPeriod = () => {
        if (!currentPeriodSource) return;

        const errors = {};
        const { amount, startDate, endDate, endType, duration, lostEarningCapacity } = currentPeriodSource;

        // Normal Period Income
        if (!amount || parseFloat(amount) <= 0) {
            errors.amount = 'Must be a positive number.';
        }

        // Start Date
        if (!startDate) {
            errors.startDate = 'Start date is required.';
        }

        // End Date required if time period is END_DATE
        if (endType === TimePeriodEndType.END_DATE && !endDate) {
            errors.endDate = 'End date is required.';
        }

        // End Date vs Start Date
        if (endType === TimePeriodEndType.END_DATE && endDate && startDate) {
            if (new Date(endDate) < new Date(startDate)) {
                errors.endDate = 'End date cannot be before the start date.';
            }
        }

        // Duration
        if (endType === TimePeriodEndType.DURATION) {
            if (!duration || parseInt(duration, 10) < 1) {
                errors.duration = 'Duration must be 1 or more.';
            }
        }

        // Lost Earning Capacity
        if (lostEarningCapacity === null || lostEarningCapacity === undefined || lostEarningCapacity < 0 || lostEarningCapacity > 1) {
            errors.lostEarningCapacity = 'Must be between 0 and 100.';
        }


        setPeriodErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSavePeriod = () => {
        if (!validateCurrentPeriod()) {
            return; // Don't save if validation fails
        }
        let updatedPeriods;
        if (editingPeriodIndex !== null) {
            updatedPeriods = [...(values.incomePeriods || [])];
            updatedPeriods[editingPeriodIndex] = { ...currentPeriodSource };
        } else {
            updatedPeriods = [...(values.incomePeriods || []), { ...currentPeriodSource }];
        }
        setFieldValue('incomePeriods', updatedPeriods);
        handleClosePeriodsModal();
    };

    const removeIncomePeriod = (index) => {
        const updatedPeriods = (values.incomePeriods || []).filter((_, i) => i !== index);
        setFieldValue('incomePeriods', updatedPeriods);
    };


    const handleNumberChange = (field) => (e) => {
        const { value } = e.target;              // value is a string
        handlePeriodModalChange(
            field,
            value === "" ? "" : Number(value)      // preserve "" while editing
        );
    };

    return (
        <>
            {/* --- Income Periods Section --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
                <Typography variant="h6">
                    Income Periods for Loss Calculation
                </Typography>
                <Button variant="contained" onClick={handleOpenAddPeriodsModal}>
                    Add Income Period
                </Button>
            </Box>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Pay Interval</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date/Duration</TableCell>
                            <TableCell>Lost Capacity</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(values.incomePeriods || []).map((source, index) => (
                            <TableRow key={source.trackingKey}>
                                <TableCell>{source.description}</TableCell>
                                <TableCell>{`$${Number(source.amount).toLocaleString()}`}</TableCell>
                                <TableCell>{source.payInterval.replace(/([A-Z])/g, ' $1').trim()}</TableCell>
                                <TableCell>{source.startDate}</TableCell>
                                <TableCell>
                                    {source.endType === TimePeriodEndType.END_DATE
                                        ? source.endDate
                                        : `${source.duration} ${source.durationUnit}`}
                                </TableCell>
                                <TableCell>{(source.lostEarningCapacity * 100).toFixed(0)}%</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenEditPeriodsModal(source, index)} size="small">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => removeIncomePeriod(index)} size="small">
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>


            {/* --- Modals --- */}

            {/* Income Periods Modal */}
            <Dialog open={periodsModalOpen} onClose={handleClosePeriodsModal} maxWidth="md" fullWidth>
                <DialogTitle>{editingPeriodIndex !== null ? 'Edit' : 'Add New'} Income Period</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {currentPeriodSource && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 8 }}>
                                <TextField label="Income Source Description" fullWidth value={currentPeriodSource.description} onChange={(e) => handlePeriodModalChange('description', e.target.value)} margin="dense" size="small" />
                            </Grid>
                            <Grid size={{ xs: 4}}>
                                <TextField
                                    label="Normal Period Income"
                                    type="number"
                                    fullWidth
                                    value={currentPeriodSource.amount}
                                    onChange={(e) => handlePeriodModalChange('amount', e.target.value)}
                                    margin="dense"
                                    size="small"
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    error={!!periodErrors.amount}
                                    helperText={periodErrors.amount}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 4}}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Pay Interval</InputLabel>
                                    <Select value={currentPeriodSource.payInterval} label="Pay Interval" onChange={(e) => handlePeriodModalChange('payInterval', e.target.value)}>
                                        {Object.values(PayInterval).map(pi => (<MenuItem key={pi} value={pi}>{pi.replace(/([A-Z])/g, ' $1').trim()}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 4}}>
                                <DatePicker
                                    label="Start Date"
                                    value={stringToDate(currentPeriodSource.startDate)}
                                    onChange={(date) => handlePeriodModalChange('startDate', dateToString(date))}
                                    format="yyyy-MM-dd"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            margin: "dense",
                                            size: "small",
                                            error: !!periodErrors.startDate,
                                            helperText: periodErrors.startDate,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 4}}>
                                <TextField
                                    label="Lost Earning Capacity"
                                    type="number"
                                    fullWidth
                                    value={(currentPeriodSource.lostEarningCapacity * 100).toFixed(0)}
                                    onChange={(e) => handlePeriodModalChange('lostEarningCapacity', parseFloat(e.target.value) / 100)}
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    margin="dense"
                                    size="small"
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    error={!!periodErrors.lostEarningCapacity}
                                    helperText={periodErrors.lostEarningCapacity}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControl component="fieldset" margin="dense" fullWidth>
                                    <RadioGroup row value={currentPeriodSource.endType} 
                                    onChange={(e) => handlePeriodModalChange('endType', e.target.value)}>
                                        <FormControlLabel value={TimePeriodEndType.END_DATE} control={<Radio />} label="On End Date" />
                                        <FormControlLabel value={TimePeriodEndType.DURATION} control={<Radio />} label="After Duration" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            {currentPeriodSource.endType === TimePeriodEndType.END_DATE && (
                                <Grid size={{ xs: 4}}>
                                    <DatePicker
                                        label="End Date"
                                        value={stringToDate(currentPeriodSource.endDate)}
                                        onChange={(date) => handlePeriodModalChange('endDate', dateToString(date))}
                                        format="yyyy-MM-dd"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                margin: "dense",
                                                size: "small",
                                                error: !!periodErrors.endDate,
                                                helperText: periodErrors.endDate,
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            {currentPeriodSource.endType === TimePeriodEndType.DURATION && (
                                <>
                                    <Grid size={{ xs: 4 }}>
                                        <TextField
                                            label="Duration"
                                            type="text"
                                            fullWidth
                                            value={currentPeriodSource.duration ?? 1}
                                            onChange={handleNumberChange("duration")}
                                            onBlur={e => handlePeriodModalChange( 'duration', Number(e.target.value) )}
                                            margin="dense"
                                            size="small"
                                            error={!!periodErrors.duration}
                                            helperText={periodErrors.duration}
                                            required
                                            inputProps={{
                                                inputMode: "numeric",
                                                pattern: "[0-9]*"
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <FormControl fullWidth margin="dense" size="small">
                                            <InputLabel>Unit</InputLabel>
                                            <Select value={currentPeriodSource.durationUnit} label="Unit" onChange={(e) => handlePeriodModalChange('durationUnit', e.target.value)}>
                                                {Object.values(DurationUnit).map(du => (<MenuItem key={du} value={du}>{du}</MenuItem>))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePeriodsModal}
                    variant='outlined'
                    >Cancel</Button>
                    <Button onClick={handleSavePeriod} 
                    variant="contained" 
                    color='primary'
                    disabled={Object.keys(periodErrors).length > 0}>Save Period</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EconomicLossesStep;